import { checkDate } from "common/helpers/Date";
import { AM, round } from "common/helpers/Math";
import { DateStringNumber, Nullable } from "common/records";
import { ICalculatedFinancingData } from "common/records/CalculatedFinancing";
import { EstimateRecord, IEstimateData } from "common/records/Estimate";
import { financeOptionValidationFromJSON } from "common/records/FinanceOptionValidation";
import { hasOwnProperty } from "common/reducers/UtilsTypes";
import { Parser } from "hot-formula-parser";
import { List, Record } from "immutable";

export const financeOptionFromJSON = (json: Partial<IFinanceOptionData>): FinanceOptionRecord => {
  const record: IFinanceOptionRecord = { ...(json as any) };

  if (json.validation && !Record.isRecord(json.validation)) {
    record.validation = financeOptionValidationFromJSON(json.validation);
  }

  if (!json.validation) {
    record.validation = financeOptionValidationFromJSON({});
  }

  if (json.archived_at) {
    record.archived_at = checkDate(json.archived_at);
  }

  if (json.created_at) {
    record.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    record.updated_at = checkDate(json.updated_at);
  }

  return new FinanceOptionRecord(record);
};

export interface IFinanceOptionData {
  id: number;
  name: string;
  estimate_id: number;
  org_id: number;
  org_uid: string;
  provider: FinanceOptionProvider;
  integrations_identifier: string;
  rate: number;
  sort_order: number;
  status: FinanceOptionStatus;
  term: number;
  formula: Nullable<string>;
  apply_now_url: Nullable<string>;
  merchant_fee: number;
  promo_fee: number;
  promo_rate: number;
  promo_term: number;
  source: any;
  validation: any;

  archive_number: Nullable<string>;
  archived_at: Nullable<DateStringNumber>;
  archived: boolean;

  created_at: DateStringNumber;
  updated_at: DateStringNumber;
}

// No good way to not duplicate things
export type FinanceOptionStatus = "active" | "inactive" | "active_range";
export const FinanceOptionStatuses = List<FinanceOptionStatus>(["active", "inactive", "active_range"]);

// No good way to not duplicate things
export type FinanceOptionProvider = "" | "fin_mkt" | "fin_mkt_offer";
export const FinanceOptionProviders = List<FinanceOptionProvider>(["fin_mkt", "fin_mkt_offer"]);
export const LocalCalcFinanceOptionProviders = List<FinanceOptionProvider>([""]);

export const calculateRate = (financeOption: FinanceOptionRecord, estimate: EstimateRecord | IEstimateData) => {
  if (!financeOption) {
    return 0;
  }

  let rate = 0;
  if (!financeOption.formula) {
    const financedAmount = estimate.payment_terms ? estimate.payment_terms.financed_amount : 0;
    if (!financeOption.rate) {
      rate = financeOption.term ? round(financedAmount / financeOption.term, 0) : 0;
    } else {
      rate = financeOption.term ? round(AM(financedAmount, financeOption.rate, financeOption.term, 1), 0) : 0;
    }
  } else {
    const parser = setupFormulaParser(estimate);
    const result = parser.parse(financeOption.formula).result;

    rate = typeof result === "number" && Number.isFinite(result) ? round(result, 0) : 0;
  }

  return rate;
};

export const filterByValidation = (
  financeOptions: List<FinanceOptionRecord>,
  estimate: EstimateRecord | IEstimateData,
  jobState: string,
): List<FinanceOptionRecord> =>
  financeOptions.filter((financeOption) => {
    const validation = financeOption.validation;
    if (!validation?.active) {
      return true;
    }

    const { min_amount, max_amount, min_payment, state } = validation;
    const financedAmount = estimate.payment_terms?.financed_amount ?? 0;

    if (state !== "" && jobState !== state) {
      return false;
    }
    if (min_amount >= 0 && financedAmount < min_amount) {
      return false;
    }
    // 0 means "no maximum".
    if (max_amount > 0 && financedAmount > max_amount) {
      return false;
    }
    if (min_payment >= 0 && calculateRate(financeOption, estimate) < min_payment) {
      return false;
    }

    return true;
  });

export const localCalculations = (
  financeOptions: List<FinanceOptionRecord>,
  estimate: EstimateRecord | IEstimateData,
): ICalculatedFinancingData[] => {
  let financedAmount = 0;
  if (Record.isRecord(estimate)) {
    financedAmount = estimate.getIn(["payment_terms", "financed_amount"]);
  } else {
    financedAmount = estimate.payment_terms.financed_amount;
  }

  const financeData = financeOptions.map((fo) => ({
    error: "",
    estimate_id: estimate.id,
    finance_option_id: fo.id,
    financed_amount: financedAmount,
    monthly_payment: calculateRate(fo, estimate),
    promo_payment: 0,
  }));

  return financeData.toArray();
};

export const setupFormulaParser = (estimate: EstimateRecord | IEstimateData) => {
  const parser = new Parser();

  const keys = ["labor_total", "product_total", "subtotal", "taxable_amount", "total"];
  for (const k of keys) {
    let value;
    if (Record.isRecord(estimate)) {
      value = estimate.getIn([k]);
    } else if (hasOwnProperty(estimate, k)) {
      value = estimate[k];
    }

    parser.setVariable(k, value);
  }

  const financedAmount = estimate.payment_terms ? estimate.payment_terms.financed_amount : 0;
  parser.setVariable("financed_amount", financedAmount);

  return parser;
};

export interface IFinanceOptionRecord {
  id: number;
  name: string;
  estimate_id: Nullable<number>;
  org_id: number;
  org_uid: string;
  provider: FinanceOptionProvider;
  integrations_identifier: string;
  rate: Nullable<number>;
  sort_order: Nullable<number>;
  status: FinanceOptionStatus;
  term: Nullable<number>;
  formula: string;
  apply_now_url: string;
  merchant_fee: number;
  promo_fee: number;
  promo_rate: number;
  promo_term: number;
  source: any;
  validation: any;

  archive_number: Nullable<string>;
  archived_at: Nullable<Date>;
  archived: boolean;

  created_at: Nullable<Date>;
  updated_at: Nullable<Date>;
}

export const defaultFinanceOptionProps = {
  id: 0,
  name: "",
  estimate_id: null,
  org_id: 0,
  org_uid: "",
  provider: "" as FinanceOptionProvider,
  integrations_identifier: "",
  rate: null,
  sort_order: null,
  status: "active" as FinanceOptionStatus,
  term: null,
  formula: "",
  apply_now_url: "",
  merchant_fee: 0,
  promo_fee: 0,
  promo_rate: 0,
  promo_term: 0,
  source: {},
  validation: financeOptionValidationFromJSON({}),
  archive_number: "",
  archived_at: null,
  archived: false,
  created_at: null,
  updated_at: null,
};

export class FinanceOptionRecord
  extends Record<IFinanceOptionRecord>(defaultFinanceOptionProps)
  implements IFinanceOptionRecord {}
